import { useRef, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import usePlaylist from 'hooks/usePlaylist';
import useGetOfficialSiteURL from 'hooks/useGetOfficialSiteURL';
import useLessonListOpen from './useLessonListOpen';
import useLessonEventTrack, {
  getLessonEventTrackData,
} from './useLessonEventTrack';

import { updateCourseProgress } from 'reduxModules/course/action';
import { push, replace } from 'reduxModules/navigation/action';

import {
  COURSE_LEARNING_LESSON_TEMPLATE,
  COURSE_INFORMATION_LESSON_TEMPLATE,
} from 'constants/routingPath';

import history from 'history.js';
import usePaywallModalControl from './usePaywallModalControl';
import { COURSE } from 'constants/tracingEvent';

const checkLessonIsPreview = ({ userPurchased, isPreview }) => {
  return !userPurchased && isPreview;
};

const checkPreviewLessonIsTimeout = ({
  userPurchased,
  isPreview,
  currentTime,
  previewStartSeconds,
  previewEndSeconds,
}) => {
  if (!checkLessonIsPreview({ userPurchased, isPreview })) return false;

  if (previewStartSeconds !== 0 && currentTime < previewStartSeconds)
    return true;

  if (previewEndSeconds !== 0 && currentTime > previewEndSeconds) return true;

  return false;
};

const getInitialSecondFormCurrentLesson = ({
  userPurchased,
  progressDuration,
  isPreview,
  previewStartSeconds,
}) => {
  if (
    !checkLessonIsPreview({ userPurchased, isPreview }) ||
    previewStartSeconds === 0
  )
    return progressDuration;

  return previewStartSeconds;
};

export default function useCoursePlaylist({
  id: courseId,
  isLearningPage,
  preparedLessonList: lessonPlaylist,
  title,
  userPurchased,
  initialLessonId,
  isCoursePage,
  trailerLesson,
}) {
  const dispatch = useDispatch();
  const getOfficialSiteURL = useGetOfficialSiteURL();
  const playerRef = useRef(null);
  const youtubeRef = useRef(null);
  const playerIsOnLoaded = useRef(false);

  const {
    isLessonListOpen,
    lessonListIsPopupMode,
    onLessonListPopupModeOpen,
    onLessonListPopupModeClose,
  } = useLessonListOpen(playerRef);

  const {
    lessonEventTrackData,
    setLessonEventTrackData,
    setLessonPageEventTrackData,
    setIsLessonClicked,
  } = useLessonEventTrack({
    commonEventTrackData: {
      courseId,
      courseName: title,
      purchaseStatus: userPurchased,
    },
    isCoursePage,
  });

  const noAutoPlay = (userPurchased && !isLearningPage) || isCoursePage;
  const lessonPath = isLearningPage
    ? COURSE_LEARNING_LESSON_TEMPLATE
    : COURSE_INFORMATION_LESSON_TEMPLATE;

  const { location } = history;

  const goLessonPage = useCallback(
    lessonId =>
      dispatch(
        replace(
          `${getOfficialSiteURL(lessonPath, {
            courseId,
            lessonId,
          })}${location?.hash}`,
        ),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [courseId, lessonPath],
  );

  const getVideoController = isTrailer => {
    if (isTrailer)
      return {
        ref: youtubeRef,
        play: ref => ref.current.internalPlayer.playVideo(),
        pause: ref => ref.current.internalPlayer.pauseVideo(),
      };

    return {
      ref: playerRef,
      play: ref => ref.current.handlePlay(),
      pause: ref => ref.current.handlePause(),
    };
  };

  const getWatchable = useCallback(
    currentLesson => !currentLesson.isLocked,
    [],
  );

  const onPlayCurrentLesson = useCallback(() => {
    setLessonEventTrackData(state => ({
      ...state,
      isStartPlay: true,
    }));
  }, [setLessonEventTrackData]);

  const onClickLessonWrapper = (lesson, lessonIndex) => {
    if (!isCoursePage) {
      setLessonPageEventTrackData({
        lessonId: lesson.id,
        lessonName: lesson.name,
        freeLesson: lesson.isFree,
      });
    }
    setLessonEventTrackData(
      getLessonEventTrackData({
        lesson,
        index: lessonIndex,
        autoPlay: false,
      }),
    );
  };

  const onPlayNextLesson = useCallback(
    nextLesson => {
      setLessonEventTrackData(
        getLessonEventTrackData({
          lesson: nextLesson,
          autoPlay: true,
        }),
      );
    },
    [setLessonEventTrackData],
  );

  const {
    currentLesson,
    setCurrentLesson,
    nextLesson,
    playCurrentLesson,
    pauseCurrentLesson,
    playNextLesson,
    onEndedLesson,
    showAutoPlayController,
    getIsCurrent,
    onProgressDragStart,
    onProgressDragEnd,
    onForwardStart,
    onRewindStart,
    onSeekEnd,
  } = usePlaylist({
    type: COURSE,
    playerRef,
    id: courseId,
    initialLessonId,
    lessonPlaylist,
    getWatchable,
    onlyControlByUrl: !isLearningPage,
    onPlayCurrentLesson,
    noAutoPlay,
    goLessonPage,
    onPlayNextLesson,
    trailerLesson,
  });

  const {
    isOpenPaywallModal,
    openPaywallModal,
    closePaywallModal,
  } = usePaywallModalControl(courseId, title, currentLesson);

  // eslint-disable-next-line no-shadow
  const setCurrentTimeToAvailable = ({ currentTime, currentLesson }) => {
    if (
      checkPreviewLessonIsTimeout({
        currentTime,
        userPurchased,
        ...currentLesson,
      }) &&
      playerIsOnLoaded.current
    ) {
      if (!playerRef.current) return;
      playerRef.current.handleSeek(currentLesson.previewStartSeconds);

      window.setTimeout(() => {
        playerRef.current.handlePause();
        onEndedLesson({ currentTime });
      }, 0);

      openPaywallModal();
    }
  };

  const initialSeconds = getInitialSecondFormCurrentLesson({
    userPurchased,
    ...currentLesson,
  });

  const onClickLesson = useCallback(
    (lesson, index) => {
      const { comingSoon, id, isTrailer, playInLearningPage } = lesson;

      if (comingSoon) {
        return null;
      }

      if (playInLearningPage) {
        return dispatch(
          push(
            getOfficialSiteURL(COURSE_LEARNING_LESSON_TEMPLATE, {
              courseId,
              lessonId: id,
            }),
          ),
        );
      }

      setIsLessonClicked(true);

      if (getIsCurrent(lesson)) {
        const { ref, play, pause } = getVideoController(isTrailer);
        if (!ref.current) return null;
        return currentLesson.isPlaying ? pause(ref) : play(ref);
      }

      const lessonPageUrl = getOfficialSiteURL(lessonPath, {
        courseId,
        lessonId: id,
      });

      dispatch(push(lessonPageUrl));

      if (isLearningPage) {
        setCurrentLesson(lesson);
      }

      onLessonListPopupModeClose();
      return setLessonEventTrackData(
        getLessonEventTrackData({
          lesson,
          autoPlay: false,
          index,
        }),
      );
    },
    [
      courseId,
      getIsCurrent,
      currentLesson.isPlaying,
      dispatch,
      getOfficialSiteURL,
      lessonPath,
      onLessonListPopupModeClose,
      setIsLessonClicked,
      setLessonEventTrackData,
      setCurrentLesson,
      isLearningPage,
    ],
  );

  const onLoadedLesson = useCallback(() => {
    playerIsOnLoaded.current = true;
  }, []);

  const updateLessonProgress = useCallback(
    ({ currentTime, seekType }) => {
      setCurrentTimeToAvailable({ currentTime, currentLesson });

      onSeekEnd({ currentTime, seekType });

      if (!userPurchased || currentTime <= currentLesson.progressDuration)
        return;

      dispatch(
        updateCourseProgress({
          courseId,
          lessonId: currentLesson.id,
          progressDuration: currentTime,
        }),
      );
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      courseId,
      currentLesson.id,
      currentLesson.progressDuration,
      userPurchased,
    ],
  );

  const onEndedCurrentLesson = useCallback(
    ({ currentTime }) => {
      if (checkLessonIsPreview({ userPurchased, ...currentLesson })) {
        openPaywallModal();
      }

      if (userPurchased && isLearningPage) {
        dispatch(
          updateCourseProgress({
            courseId,
            lessonId: currentLesson.id,
            progressDuration: currentLesson?.durationTime,
          }),
        );
      }
      setLessonEventTrackData(state => ({
        ...state,
        isEnded: true,
      }));

      if (currentLesson.isTrailer) {
        pauseCurrentLesson();
        return;
      }

      onEndedLesson({ currentTime });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      courseId,
      currentLesson.durationTime,
      currentLesson.id,
      currentLesson.isTrailer,
      dispatch,
      isLearningPage,
      pauseCurrentLesson,
      setLessonEventTrackData,
      userPurchased,
      onEndedLesson,
    ],
  );

  const onClickCurrentLessonChapter = position => {
    if (!playerRef.current) {
      return;
    }

    if (!currentLesson.isPlaying) {
      playerRef.current.handlePlay();
    }

    playerRef.current.handleSeek(position);
    onLessonListPopupModeClose();
    setLessonEventTrackData(state => ({
      ...state,
      startTime: position,
    }));
  };

  const toggleCurrentLessonChapterList = () => {
    setCurrentLesson(state => ({
      ...state,
      isChapterListOpen: !state.isChapterListOpen || false,
    }));
  };

  useEffect(() => {
    setLessonEventTrackData(state => ({
      ...state,
      freeLesson: currentLesson.isFree,
      lessonId: currentLesson.id,
      lessonName: currentLesson.title,
      lessonDuration: currentLesson.durationTime,
      lessonMaterialNum: currentLesson.materials?.length,
    }));
  }, [currentLesson]);

  return {
    currentLesson: {
      ...currentLesson,
      initialSeconds,
    },
    isOpenPaywallModal,
    openPaywallModal,
    closePaywallModal,
    isLessonListOpen,
    lessonEventTrackData,
    lessonListIsPopupMode,
    nextVideoTitle: nextLesson.name,
    onClickCurrentLessonChapter,
    onClickLesson,
    onClickLessonWrapper,
    onLoadedLesson,
    updateLessonProgress,
    onEndedCurrentLesson,
    pauseCurrentLesson,
    playCurrentLesson,
    onLessonListPopupModeClose,
    onLessonListPopupModeOpen,
    playerRef,
    playNextLesson,
    setLessonEventTrackData,
    showAutoPlayController,
    toggleCurrentLessonChapterList,
    youtubeRef,
    onProgressDragStart,
    onProgressDragEnd,
    onForwardStart,
    onRewindStart,
  };
}
